import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import OrderStatusBadge from './OrderStatusBadge';
import PaymentStatusBadge from './PaymentStatusBadge';

const OrderTable = ({ orders, onViewDetails, onDownloadInvoice, onTrackOrder, onReorder }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'orderDate', direction: 'desc' });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })?.format(new Date(date));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = React.useMemo(() => {
    let sortableOrders = [...orders];
    if (sortConfig?.key) {
      sortableOrders?.sort((a, b) => {
        if (sortConfig?.key === 'orderDate') {
          const aDate = new Date(a[sortConfig.key]);
          const bDate = new Date(b[sortConfig.key]);
          return sortConfig?.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        if (sortConfig?.key === 'totalAmount') {
          return sortConfig?.direction === 'asc' ? a?.[sortConfig?.key] - b?.[sortConfig?.key] : b?.[sortConfig?.key] - a?.[sortConfig?.key];
        }
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOrders;
  }, [orders, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  if (orders?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="ShoppingBag" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Orders Found</h3>
        <p className="text-muted-foreground mb-6">
          You haven't placed any orders yet or no orders match your current filters.
        </p>
        <Link to="/product-catalog">
          <Button variant="default" iconName="Plus" iconPosition="left">
            Browse Products & Services
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('orderNumber')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Order #</span>
                  {getSortIcon('orderNumber')}
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('orderDate')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Date</span>
                  {getSortIcon('orderDate')}
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Services</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('totalAmount')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Total</span>
                  {getSortIcon('totalAmount')}
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Payment</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="text-right p-4">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders?.map((order) => (
              <tr key={order?.id} className="border-b border-border hover:bg-muted/20 transition-smooth">
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-sm font-medium text-foreground">
                      #{order?.orderNumber}
                    </span>
                    {order?.isUrgent && (
                      <Icon name="Zap" size={14} className="text-warning" />
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{formatDate(order?.orderDate)}</span>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    {order?.items?.slice(0, 2)?.map((item, index) => (
                      <div key={index} className="text-sm text-foreground">
                        {item?.name}
                      </div>
                    ))}
                    {order?.items?.length > 2 && (
                      <div className="text-xs text-muted-foreground">
                        +{order?.items?.length - 2} more items
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className="font-semibold text-foreground">
                    {formatCurrency(order?.totalAmount)}
                  </span>
                </td>
                <td className="p-4">
                  <PaymentStatusBadge 
                    status={order?.paymentStatus} 
                    method={order?.paymentMethod}
                    size="sm"
                  />
                </td>
                <td className="p-4">
                  <OrderStatusBadge status={order?.status} size="sm" />
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(order)}
                      iconName="Eye"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadInvoice(order)}
                      iconName="Download"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onTrackOrder(order)}
                      iconName="MapPin"
                    />
                    {order?.status === 'completed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReorder(order)}
                        iconName="RotateCcw"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedOrders?.map((order) => (
          <div key={order?.id} className="border border-border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-mono text-sm font-medium text-foreground">
                  #{order?.orderNumber}
                </span>
                {order?.isUrgent && (
                  <Icon name="Zap" size={14} className="text-warning" />
                )}
              </div>
              <span className="text-sm text-muted-foreground">
                {formatDate(order?.orderDate)}
              </span>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Services:</div>
              {order?.items?.slice(0, 2)?.map((item, index) => (
                <div key={index} className="text-sm text-muted-foreground">
                  • {item?.name}
                </div>
              ))}
              {order?.items?.length > 2 && (
                <div className="text-xs text-muted-foreground">
                  +{order?.items?.length - 2} more items
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <PaymentStatusBadge 
                  status={order?.paymentStatus} 
                  method={order?.paymentMethod}
                  size="sm"
                />
                <OrderStatusBadge status={order?.status} size="sm" />
              </div>
              <div className="text-right">
                <div className="font-semibold text-foreground">
                  {formatCurrency(order?.totalAmount)}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(order)}
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownloadInvoice(order)}
                iconName="Download"
                iconPosition="left"
                fullWidth
              >
                Invoice
              </Button>
              {order?.status === 'completed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReorder(order)}
                  iconName="RotateCcw"
                  iconPosition="left"
                  fullWidth
                >
                  Reorder
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderTable;